import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { extend } from '@thd-nucleus/data-sources';
import {
  Tile, TileContent, TileText
} from '@one-thd/sui-atomic-components';
import {
  ConfirmedFilled, Delivery, DeliveryExpress, InStorePickup, Store
} from '@one-thd/sui-icons';
import { StoreSelector } from '../StoreSelector/StoreSelector';
import '../../styles/availability-filters.scss';
import { ProductResultsContext } from '../ProductResultsContext';
import { storeLifecycleEvent } from '../../product-results-helpers';

const AvailabilityRefinement = ({
  refinement,
  onDisplayData,
  active,
  isMobile,
  id,
  onChange,
  dimension
}) => {

  const { selected } = refinement;
  const { onDisplay, onDisplayChange, itemCount } = onDisplayData;
  const { data } = useContext(ProductResultsContext);
  const { isStoreDisplay } = data?.searchModel?.metadata || false;

  const isTileSelected = selected || active;
  const isOnDisplayInUrl = onDisplay === 'true';

  const isOnDisplayTile = () => {
    return refinement?.label === 'Pick Up Today' && isStoreDisplay === 'true';
  };

  const isTileClicked = isOnDisplayTile() ? isOnDisplayInUrl : isTileSelected;
  const isUnavailable = isOnDisplayTile()
    ? parseInt(itemCount, 10) === 0 : parseInt(refinement?.recordCount, 10) === 0;

  const onChangeForOnDisplay = () => {
    // when onDisplay filters is selected/removed from leftNav/hortizontal-getItFast
    if (onDisplayChange) {
      storeLifecycleEvent('filters-refinements-visualNav.click', {
        dimensionName: 'Get It Fast',
        label: 'On Display',
        refinementLabel: 'On Display',
        refinementKey: '',
        selected: onDisplay === 'false' ? null : true
      });

      return (!!data && onDisplay === 'false') ? onDisplayChange({ useStoreFilter: 'true' })
        : onDisplayChange({ useStoreFilter: 'false' });
    }
    return null;
  };

  const onChangeForTileClick = () => {
    if (onChange) {
      if (isOnDisplayTile()) {
        onChangeForOnDisplay();
      } else {
        onChange({
          refinement, dimension, onDisplay
        });
      }
    }
  };

  const generateRefinementLabel = () => {
    let displayLabel = refinement?.label;

    if (refinement?.refinementKey === '1z175a5') {
      displayLabel = isStoreDisplay === 'false'
        ? 'In Stock at Store Today'
        : 'On Display In Store';
    }

    return (
      <>
        {displayLabel}
      </>
    );
  };

  const generateIcon = () => {
    const { refinementKey } = refinement || null;
    // Pick Up Today : 1z175a5
    // Free 2-Day Delivery : bwo6i
    // Same-Day Delivery : 1z175cq
    if (refinementKey === '1z175a5' && isStoreDisplay === 'false') {
      return <InStorePickup color="primary" />;
    }
    if (refinementKey === '1z175a5' && isStoreDisplay === 'true') {
      return <Store color="primary" />;
    }
    if (refinementKey === 'bwo6i') {
      return <Delivery color="primary" />;
    }
    if (refinementKey === '1z175cq') {
      return <DeliveryExpress color="primary" />;
    }
    return null;
  };

  return (
    <>
      <Tile
        value={refinement?.label}
        aria-label={refinement?.label}
        onChange={onChangeForTileClick}
        id={`${id}-${refinement?.label}`}
        key={`${id}-${refinement?.label}`}
        disabled={isUnavailable}
        data-testid="availability-refinements-btn"
        selected={isTileClicked}
        style={isTileClicked ? {
          backgroundColor: 'rgba(255, 255, 255, 1)'
        } : {}}
      >
        <TileContent
          alignItems={isMobile ? 'center' : 'start'}
          grow
        >
          {generateIcon()}
          <div className="sui-inline-block sui-pt-1 sui-whitespace-nowrap">
            <TileText>
              {generateRefinementLabel()}
            </TileText>
          </div>
          {isTileClicked && (
            <span className="sui-mt-1 sui-pt-1px">
              <ConfirmedFilled size="small" />
            </span>
          )}
        </TileContent>
      </Tile>
    </>
  );
};

AvailabilityRefinement.propTypes = {
  dimension: PropTypes.shape({}),
  refinement: PropTypes.shape({
    label: PropTypes.string,
    recordCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    refinementKey: PropTypes.string,
    selected: PropTypes.bool,
    url: PropTypes.string
  }),
  onDisplayData: PropTypes.shape({
    onDisplay: PropTypes.string,
    onDisplayChange: PropTypes.func,
    itemCount: PropTypes.string
  }),
  active: PropTypes.bool,
  isMobile: PropTypes.bool,
  id: PropTypes.string,
  onChange: PropTypes.func
};

AvailabilityRefinement.defaultProps = {
  dimension: null,
  refinement: null,
  onDisplayData: {
    onDisplay: 'false',
    onDisplayChange: null,
    itemCount: null
  },
  active: false,
  isMobile: false,
  id: null,
  onChange: null
};

AvailabilityRefinement.displayName = 'AvailabilityRefinement';

AvailabilityRefinement.dataModel = extend(StoreSelector);

export { AvailabilityRefinement };
